<div class="session-starter">
  <div class="card">
    <!-- Step 1: Add Players and Configure Session -->
    <div
      *ngIf="(!activeSession || forceCreateMode) && !sessionReady"
      class="start-session-form"
    >
      <div class="form-group">
        <label for="sessionName">Session Name</label>
        <input
          id="sessionName"
          type="text"
          [(ngModel)]="sessionName"
          placeholder="e.g., Thursday GT3 Practice"
          class="input-field"
          maxlength="80"
        />
      </div>

      <div class="form-group">
        <label for="visibility">Who can join?</label>
        <select id="visibility" [(ngModel)]="visibility" class="input-field">
          <option *ngIf="hasTeamContext" value="team">Team Members Only</option>
          <option value="public">Public (any authenticated user)</option>
        </select>
        <small *ngIf="!hasTeamContext" class="help-text">
          No team selected. This session will be public.
        </small>
      </div>

      <!-- Track Selection with Typeahead -->
      <div class="form-group">
        <label for="track">Track</label>
        <div class="typeahead-container">
          <input
            id="track"
            type="text"
            [(ngModel)]="trackSearchQuery"
            (input)="filterTracks()"
            (focus)="onTrackInputFocus()"
            (blur)="onTrackInputBlur()"
            placeholder="Search for a track..."
            class="input-field"
            autocomplete="off"
            autofocus
          />
          <div
            *ngIf="showTrackDropdown && filteredTracks.length > 0"
            class="typeahead-dropdown"
          >
            <div
              *ngFor="let track of filteredTracks.slice(0, 50)"
              class="typeahead-item"
              (click)="selectTrack(track)"
            >
              {{ getTrackDisplay(track) }}
            </div>
          </div>
          <div
            *ngIf="
              showTrackDropdown &&
              trackSearchQuery &&
              filteredTracks.length === 0
            "
            class="typeahead-empty"
          >
            No tracks found
          </div>
        </div>
        <small class="help-text"
          >Type to search for the track you'll be racing on</small
        >
      </div>

      <!-- Season Selection -->
      <div class="form-group" *ngIf="seasons.length > 0">
        <label for="season">Season (Optional)</label>
        <select id="season" [(ngModel)]="selectedSeasonId" class="input-field">
          <option [ngValue]="undefined">No Season</option>
          <option *ngFor="let season of seasons" [ngValue]="season.id">
            {{ season.name }}
          </option>
        </select>
        <small class="help-text"
          >Link this session to a season for championship tracking</small
        >
      </div>

      <div class="form-row">
        <div class="form-group">
          <label for="lapsPerDriver">Laps Per Driver</label>
          <input
            id="lapsPerDriver"
            [(ngModel)]="lapsPerDriver"
            class="input-field"
            type="number"
            min="1"
            step="1"
          />
        </div>
      </div>

      <div class="optional-section">
        <button
          type="button"
          class="optional-toggle"
          [class.open]="!allowSetupChanges"
          (click)="allowSetupChanges = !allowSetupChanges"
        >
          <span class="optional-toggle-title">Fixed Setup Enforcement</span>
          <span class="optional-toggle-meta">{{
            allowSetupChanges ? "Off" : "On"
          }}</span>
          <span
            class="optional-switch"
            [class.on]="!allowSetupChanges"
            aria-hidden="true"
          >
            <span class="optional-switch-thumb"></span>
          </span>
          <span class="optional-toggle-chevron">⌄</span>
        </button>

        <div class="optional-panel" *ngIf="!allowSetupChanges">
          <small class="help-text">
            Drivers must use an unmodified iRacing setup. If the telemetry
            client reports a modified setup, laps will be rejected and the
            overlay will show a tainted setup warning.
          </small>
        </div>
      </div>

      <div class="optional-section">
        <button
          type="button"
          class="optional-toggle"
          [class.open]="enforceStaticWeather"
          (click)="toggleStaticWeatherEnforcement()"
        >
          <span class="optional-toggle-title">Enforce Static Weather</span>
          <span class="optional-toggle-meta">{{
            enforceStaticWeather ? "On" : "Off"
          }}</span>
          <span
            class="optional-switch"
            [class.on]="enforceStaticWeather"
            aria-hidden="true"
          >
            <span class="optional-switch-thumb"></span>
          </span>
          <span class="optional-toggle-chevron">⌄</span>
        </button>

        <div class="optional-panel" *ngIf="enforceStaticWeather">
          <small class="help-text">
            Leave this off to use the default test drive weather. When enabled,
            telemetry must report a matching static weather setup before laps
            will count.
          </small>

          <div class="form-row">
            <div class="form-group">
              <label for="staticWeatherSkies">Skies</label>
              <select
                id="staticWeatherSkies"
                [(ngModel)]="staticWeatherSkies"
                class="input-field"
              >
                <option
                  *ngFor="let option of staticWeatherSkyOptions"
                  [ngValue]="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label for="staticTrackMoisture">Track Moisture</label>
              <select
                id="staticTrackMoisture"
                [(ngModel)]="staticTrackMoisture"
                class="input-field"
              >
                <option
                  *ngFor="let option of staticTrackMoistureOptions"
                  [ngValue]="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="staticAirTempC"
                >Temperature {{ formatTemperatureWithFahrenheit(staticAirTempC) }}</label
              >
              <input
                id="staticAirTempC"
                [(ngModel)]="staticAirTempC"
                class="input-field"
                type="number"
                step="0.1"
              />
            </div>

            <div class="form-group">
              <label for="staticRelativeHumidityPct">Humidity (%)</label>
              <input
                id="staticRelativeHumidityPct"
                [(ngModel)]="staticRelativeHumidityPct"
                class="input-field"
                type="number"
                min="0"
                max="100"
                step="0.1"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="staticWindSpeedMps">Wind Speed (m/s)</label>
              <input
                id="staticWindSpeedMps"
                [(ngModel)]="staticWindSpeedMps"
                class="input-field"
                type="number"
                min="0"
                step="0.1"
              />
            </div>

            <div class="form-group">
              <label for="staticWindDirection">Wind Direction</label>
              <select
                id="staticWindDirection"
                [(ngModel)]="staticWindDirection"
                class="input-field"
              >
                <option
                  *ngFor="let option of staticWindDirectionOptions"
                  [ngValue]="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div class="optional-section">
        <button
          type="button"
          class="optional-toggle"
          [class.open]="restrictByIRating"
          (click)="toggleIRatingRestrictions()"
        >
          <span class="optional-toggle-title">Restrict Session by iRating</span>
          <span class="optional-toggle-meta">{{
            restrictByIRating ? "On" : "Off"
          }}</span>
          <span
            class="optional-switch"
            [class.on]="restrictByIRating"
            aria-hidden="true"
          >
            <span class="optional-switch-thumb"></span>
          </span>
          <span class="optional-toggle-chevron">⌄</span>
        </button>

        <div class="optional-panel" *ngIf="restrictByIRating">
          <small class="help-text">
            Restrict session participants by iRating range.
          </small>
          <div class="form-row">
            <div class="form-group">
              <label for="minIRating">Minimum iRating</label>
              <input
                id="minIRating"
                [(ngModel)]="minIRating"
                class="input-field"
                type="number"
                min="0"
                placeholder="e.g. 1200"
              />
            </div>

            <div class="form-group">
              <label for="maxIRating">Maximum iRating</label>
              <input
                id="maxIRating"
                [(ngModel)]="maxIRating"
                class="input-field"
                type="number"
                min="0"
                placeholder="e.g. 3000"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="optional-section">
        <button
          type="button"
          class="optional-toggle"
          [class.open]="enableScheduleWindow"
          (click)="toggleScheduleWindow()"
        >
          <span class="optional-toggle-title">Session Date Window</span>
          <span class="optional-toggle-meta">{{
            enableScheduleWindow ? "On" : "Off"
          }}</span>
          <span
            class="optional-switch"
            [class.on]="enableScheduleWindow"
            aria-hidden="true"
          >
            <span class="optional-switch-thumb"></span>
          </span>
          <span class="optional-toggle-chevron">⌄</span>
        </button>

        <div class="optional-panel" *ngIf="enableScheduleWindow">
          <small class="help-text">
            Set optional start/end timing for this session.
          </small>
          <div class="form-row">
            <div class="form-group">
              <label for="plannedStart">Start Date</label>
              <input
                id="plannedStart"
                type="datetime-local"
                [(ngModel)]="plannedStartTime"
                class="input-field"
              />
            </div>

            <div class="form-group">
              <label for="plannedEnd">End Date</label>
              <input
                id="plannedEnd"
                type="datetime-local"
                [(ngModel)]="plannedEndTime"
                class="input-field"
              />
              <small class="help-text"
                >If set, the session will automatically end at this time.</small
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Car Class Restrictions (Optional) with Search -->
      <div class="form-group">
        <label>Allowed Car Classes (Optional)</label>
        <div class="typeahead-container">
          <input
            type="text"
            [(ngModel)]="carSearchQuery"
            (input)="filterCars()"
            (focus)="onCarInputFocus()"
            (blur)="onCarInputBlur()"
            placeholder="Search for cars or car classes..."
            class="input-field"
            autocomplete="off"
          />
          <div
            *ngIf="showCarDropdown && filteredCars.length > 0"
            class="typeahead-dropdown car-dropdown"
          >
            <div
              *ngFor="let car of filteredCars"
              class="typeahead-item car-item"
              [class.selected]="isCarClassSelected(getCarOptionValue(car))"
              (mousedown)="$event.preventDefault()"
              (click)="toggleCarClass(getCarOptionValue(car))"
            >
              <div class="car-item-header">
                <div class="car-name">{{ getCarOptionValue(car) }}</div>
                <span
                  class="car-selected-indicator"
                  *ngIf="isCarClassSelected(getCarOptionValue(car))"
                >
                  ✓
                </span>
              </div>
              <div
                class="car-classes"
                *ngIf="car.carClasses && car.carClasses.length > 0"
              >
                <span
                  *ngFor="let cc of car.carClasses; let isLast = last"
                  class="car-class-badge"
                >
                  {{ cc.carClass.shortName || cc.carClass.carName
                  }}{{ !isLast ? ", " : "" }}
                </span>
              </div>
            </div>
          </div>
          <div
            *ngIf="
              showCarDropdown && carSearchQuery && filteredCars.length === 0
            "
            class="typeahead-empty"
          >
            No cars found
          </div>
        </div>

        <!-- Selected car classes chips -->
        <div *ngIf="selectedCarClasses.length > 0" class="selected-cars">
          <div
            class="car-class-chip selected"
            *ngFor="let carClass of selectedCarClasses"
          >
            {{ carClass }}
            <button
              type="button"
              class="remove-chip"
              (click)="toggleCarClass(carClass)"
            >
              ×
            </button>
          </div>
        </div>

        <small class="help-text"
          >Leave empty to allow all cars. Car will be detected from iRacing
          telemetry.</small
        >
      </div>

      <div class="telemetry-note">
        <span class="note-icon">ℹ</span>
        <span
          >Make sure the <strong>Telemetry Client</strong> is running on your
          sim PC.</span
        >
      </div>

      <button (click)="prepareSession()" class="btn btn-primary">
        Continue
      </button>

      <p *ngIf="error" class="error">{{ error }}</p>
    </div>

    <!-- Step 2: Ready to Create Session -->
    <div
      *ngIf="(!activeSession || forceCreateMode) && sessionReady"
      class="ready-state"
    >
      <div class="ready-info">
        <div class="ready-center">
          <div class="ready-icon">✓</div>
          <h3>Session Ready</h3>
        </div>

        <div class="session-config">
          <p><strong>Name:</strong> {{ sessionName }}</p>
          <p>
            <strong>Visibility:</strong>
            {{
              visibility === "public"
                ? "Public (any authenticated user)"
                : "Team Members Only"
            }}
          </p>
          <p><strong>Track:</strong> {{ getSelectedTrackName() }}</p>
          <p *ngIf="selectedSeasonId">
            <strong>Season:</strong> {{ getSeasonName(selectedSeasonId) }}
          </p>
          <p><strong>Laps Per Driver:</strong> {{ lapsPerDriver }} laps</p>
          <p>
            <strong>Setup Changes:</strong>
            {{ allowSetupChanges ? "Allowed" : "Not allowed" }}
          </p>
          <p *ngIf="restrictByIRating">
            <strong>iRating Window:</strong>
            {{ minIRating !== undefined ? minIRating : "No minimum" }} -
            {{ maxIRating !== undefined ? maxIRating : "No maximum" }}
          </p>
          <p *ngIf="plannedStartTime">
            <strong>Start:</strong> {{ plannedStartTime | date: "medium" }}
          </p>
          <p *ngIf="plannedEndTime">
            <strong>End:</strong> {{ plannedEndTime | date: "medium" }}
          </p>
          <p *ngIf="selectedCarClasses.length > 0">
            <strong>Allowed Cars:</strong> {{ selectedCarClasses.join(", ") }}
          </p>
          <p class="info-note">
            Car will be detected from iRacing telemetry{{
              selectedCarClasses.length > 0 ? " and validated" : ""
            }}
          </p>
        </div>
      </div>

      <div class="button-group">
        <button (click)="createSession()" class="btn btn-start">
          Create Session
        </button>
        <button (click)="cancelPrepare()" class="btn btn-secondary-outline">
          Cancel
        </button>
      </div>

      <p *ngIf="error" class="error">{{ error }}</p>
    </div>

    <!-- Step 3: Session Active -->
    <div *ngIf="activeSession && !forceCreateMode" class="active-indicator">
      <div class="status-badge active">Session Active</div>
      <p class="active-text">
        <strong>{{ activeSession.name }}</strong>
      </p>
      <p class="active-text">
        Your session is running. Start a stint to begin tracking laps.
      </p>

      <!-- Session Sharing Section -->
      <div class="session-sharing">
        <h3 class="sharing-title">Session Sharing</h3>

        <!-- Session ID -->
        <div class="share-section">
          <label class="share-label">Session ID</label>
          <div class="share-box">
            <code class="session-id-code">{{ activeSession.id }}</code>
            <button
              (click)="copySessionId()"
              class="btn-copy"
              [class.copied]="copySuccess"
            >
              {{ copySuccess ? "✓ Copied!" : "📋 Copy ID" }}
            </button>
          </div>
          <p class="share-hint">
            Share this ID with others to join via telemetry client
          </p>
        </div>

        <!-- Shareable Link -->
        <div class="share-section">
          <label class="share-label">Shareable Link</label>
          <div class="share-box">
            <input
              type="text"
              [value]="getShareableLink()"
              readonly
              class="share-link-input"
            />
            <button
              (click)="copyShareableLink()"
              class="btn-copy"
              [class.copied]="copySuccess"
            >
              {{ copySuccess ? "✓ Copied!" : "🔗 Copy Link" }}
            </button>
          </div>
          <p class="share-hint">
            Share this link with others to view the session
          </p>
        </div>

        <!-- Participants List -->
        <div class="share-section">
          <label class="share-label"
            >Participants ({{ participants.length }})</label
          >
          <div class="participants-list" *ngIf="participants.length > 0">
            <div
              *ngFor="let participant of participants"
              class="participant-item"
            >
              <div class="participant-info">
                <span class="participant-name">{{ participant.name }}</span>
                <span
                  class="participant-badge"
                  [ngClass]="
                    getParticipantTypeBadgeClass(participant.playerType)
                  "
                >
                  {{ getParticipantTypeLabel(participant.playerType) }}
                </span>
              </div>
              <div class="participant-stats">
                <span class="stat"
                  >{{ participant.lapsCompleted || 0 }}/{{
                    participant.lapsAllowed || 0
                  }}
                  laps</span
                >
                <span
                  class="stat-status"
                  [class.active]="participant.isActive"
                  [class.inactive]="!participant.isActive"
                >
                  {{ participant.isActive ? "Active" : "Inactive" }}
                </span>
              </div>
            </div>
          </div>
          <div class="empty-participants" *ngIf="participants.length === 0">
            <p>Loading participants...</p>
          </div>
          <button (click)="loadParticipants()" class="btn-refresh">
            🔄 Refresh Participants
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
